import { z } from 'zod'
import { UserRole } from '../types/enums'

/**
 * TC kimlik numarası — 11 hane. Boşluk temizlenir; boş string null'a çevrilir.
 * Not: yalnızca format kontrolü; TC algoritma doğrulaması bilinçli olarak eklenmedi (§4).
 */
const nationalIdSchema = z
  .string()
  .trim()
  .regex(/^\d{11}$/, 'TC kimlik numarası 11 haneli olmalıdır.')
  .nullable()
  .optional()

/**
 * IBAN — TR IBAN formatı (TR + 24 hane). Girişte boşluklar kaldırılır, büyük harfe çevrilir.
 */
const ibanSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/\s+/g, '').toUpperCase())
  .refine((v) => /^TR\d{24}$/.test(v), 'Geçerli bir TR IBAN giriniz (TR + 24 rakam).')
  .nullable()
  .optional()

const bankNameSchema = z.string().trim().max(100).nullable().optional()

/** ISO tarih (YYYY-MM-DD); boş string null'a çevrilir. */
const isoDateSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarih YYYY-AA-GG biçiminde olmalıdır.')
  .nullable()
  .optional()

export const InviteUserSchema = z.object({
  email: z
    .string({ required_error: 'E-posta zorunludur.' })
    .email('Geçerli bir kurumsal e-posta giriniz.'),
  firstName: z
    .string({ required_error: 'Ad zorunludur.' })
    .min(1, 'Ad zorunludur.')
    .max(50),
  lastName: z
    .string({ required_error: 'Soyad zorunludur.' })
    .min(1, 'Soyad zorunludur.')
    .max(50),
  role: z.nativeEnum(UserRole, { required_error: 'Rol seçimi zorunludur.' }),
  officeId: z.string({ required_error: 'Ofis seçimi zorunludur.' }).uuid(),
  title: z.string().max(100).nullable().optional(),
  department: z.string().max(100).nullable().optional(),
  /**
   * Saatlik maliyet — float YASAK, string olarak alınır.
   * [IK-04] hourlyRate veya monthlySalary'den en az biri zorunludur.
   * monthlySalary verildiğinde backend hourlyRate'i hesaplar.
   */
  hourlyRate: z
    .string()
    .regex(/^\d+(\.\d{1,4})?$/, 'Geçerli bir maliyet değeri giriniz.')
    .optional(),
  /**
   * Aylık brüt maaş — girildiğinde saatlik maliyet otomatik hesaplanır.
   * hourlyRate = monthlySalary / (ofis çalışma günü * saat/gün * 52/12)
   */
  monthlySalary: z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/, 'Geçerli bir maaş değeri giriniz.')
    .optional(),
  /** null = ofis varsayılanı kullan — part-time için doldurulur */
  workHoursPerDay: z
    .number()
    .min(0.5, 'Günlük çalışma saati en az 0.5 olmalıdır.')
    .max(24)
    .nullable()
    .optional(),
  /** Maaş ödemesi için opsiyonel — tüm kullanıcılarda tutulabilir */
  nationalId: nationalIdSchema,
  iban: ibanSchema,
  bankName: bankNameSchema,
  hireDate: isoDateSchema,
}).refine(
  (d) => d.hourlyRate || d.monthlySalary,
  { message: 'Saatlik maliyet veya aylık maaş zorunludur.', path: ['hourlyRate'] },
)

/**
 * Girişsiz kayıt personeli oluşturma — e-posta/davet/şifre YOK.
 * Saha çalışanları için; yalnızca maaş/bordro/puantaj verileri tutulur.
 * accountType='PERSONNEL' olarak backend'de set edilir; role varsayılanı EMP.
 */
export const CreatePersonnelSchema = z.object({
  firstName: z
    .string({ required_error: 'Ad zorunludur.' })
    .min(1, 'Ad zorunludur.')
    .max(50),
  lastName: z
    .string({ required_error: 'Soyad zorunludur.' })
    .min(1, 'Soyad zorunludur.')
    .max(50),
  officeId: z.string({ required_error: 'Ofis seçimi zorunludur.' }).uuid(),
  /** Girişsiz personel de bir role sahip olmalı (NOT NULL); varsayılan EMP */
  role: z.nativeEnum(UserRole).optional(),
  title: z.string().max(100).nullable().optional(),
  department: z.string().max(100).nullable().optional(),
  hourlyRate: z
    .string()
    .regex(/^\d+(\.\d{1,4})?$/, 'Geçerli bir maliyet değeri giriniz.')
    .optional(),
  monthlySalary: z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/, 'Geçerli bir maaş değeri giriniz.')
    .optional(),
  workHoursPerDay: z.number().min(0.5).max(24).nullable().optional(),
  nationalId: nationalIdSchema,
  iban: ibanSchema,
  bankName: bankNameSchema,
  hireDate: isoDateSchema,
}).refine(
  (d) => d.hourlyRate || d.monthlySalary,
  { message: 'Saatlik maliyet veya aylık maaş zorunludur.', path: ['hourlyRate'] },
)

export const UpdateUserSchema = z.object({
  firstName: z.string().min(1).max(50).optional(),
  lastName: z.string().min(1).max(50).optional(),
  title: z.string().max(100).nullable().optional(),
  department: z.string().max(100).nullable().optional(),
  officeId: z.string().uuid().optional(),
  role: z.nativeEnum(UserRole).optional(),
  hourlyRate: z.string().regex(/^\d+(\.\d{1,4})?$/).optional(),
  monthlySalary: z.string().regex(/^\d+(\.\d{1,2})?$/).optional(),
  workHoursPerDay: z.number().min(0.5).max(24).nullable().optional(),
  /** Doğrudan yönetici — izin bildirimleri bu kişiye iletilir; null = atama kaldır */
  managerId: z.string().uuid().nullable().optional(),
  nationalId: nationalIdSchema,
  iban: ibanSchema,
  bankName: bankNameSchema,
  hireDate: isoDateSchema,
  terminationDate: isoDateSchema,
})

export const ChangeRoleSchema = z.object({
  userId: z.string().uuid({ message: 'Geçersiz kullanıcı kimliği.' }),
  role: z.nativeEnum(UserRole, { required_error: 'Yeni rol zorunludur.' }),
  reason: z.string().min(5, 'Rol değişiklik gerekçesi en az 5 karakter olmalıdır.').optional(),
})

export type InviteUserInput = z.infer<typeof InviteUserSchema>
export type UpdateUserInput = z.infer<typeof UpdateUserSchema>
export type CreatePersonnelInput = z.infer<typeof CreatePersonnelSchema>
export type ChangeRoleInput = z.infer<typeof ChangeRoleSchema>

/**
 * Masaüstü → portal: davet e-postası isteği (`POST /api/v1/license/mail/invitation`).
 * Yalnızca sabit davet şablonu gönderilir; serbest metin/HTML kabul edilmez. Alanlar tek satırdır (konu/başlık enjeksiyonu yok).
 */
const singleLine = /^[^\r\n]*$/
export const PortalInvitationMailSchema = z.object({
  to: z.string().trim().toLowerCase().email().max(254),
  firstName: z.string().trim().min(1).max(50).regex(singleLine),
  tenantName: z.string().trim().min(1).max(150).regex(singleLine),
  inviteLink: z.string().url().max(4000),
})
export type PortalInvitationMailInput = z.infer<typeof PortalInvitationMailSchema>

/**
 * Masaüstü → portal: şifre sıfırlama kodu e-postası (`POST /api/v1/license/mail/password-reset`).
 * Sabit şablon; `code` yalnızca UUID olabilir (portal serbest metin taşıyamaz).
 */
export const PortalPasswordResetMailSchema = z.object({
  to: z.string().trim().toLowerCase().email().max(254),
  firstName: z.string().trim().min(1).max(50).regex(singleLine),
  code: z.string().uuid(),
})
export type PortalPasswordResetMailInput = z.infer<typeof PortalPasswordResetMailSchema>
